import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCheckIcon, CreditCardIcon, ShipIcon, FileTextIcon, CheckCircleIcon, PlayIcon, StopCircleIcon, BarChart3Icon, ArrowRightIcon } from 'lucide-react';
export function UserFlow() {
  const navigate = useNavigate();
  const steps = [{
    id: 1,
    title: 'Add Beneficiary',
    description: 'Register new resource collector',
    icon: <UserCheckIcon size={32} data-id="element-2090" />,
    role: 'Station Officer',
    path: '/beneficiaries',
    status: 'completed' as const
  }, {
    id: 2,
    title: 'Approve Beneficiary',
    description: 'Review and approve registration',
    icon: <CheckCircleIcon size={32} data-id="element-2091" />,
    role: 'Range Officer',
    path: '/beneficiaries',
    status: 'completed' as const
  }, {
    id: 3,
    title: 'Issue Smart ID',
    description: 'Generate QR/RFID card',
    icon: <CreditCardIcon size={32} data-id="element-2092" />,
    role: 'Station Officer',
    path: '/smart-id',
    status: 'completed' as const
  }, {
    id: 4,
    title: 'Register Vessel',
    description: 'Register boat (if applicable)',
    icon: <ShipIcon size={32} data-id="element-2093" />,
    role: 'Station Officer',
    path: '/vessels',
    status: 'current' as const
  }, {
    id: 5,
    title: 'Apply Permit',
    description: 'Submit forest entry permit',
    icon: <FileTextIcon size={32} data-id="element-2094" />,
    role: 'Beneficiary/Officer',
    path: '/permits',
    status: 'upcoming' as const
  }, {
    id: 6,
    title: 'Approve Permit',
    description: 'Review and approve permit',
    icon: <CheckCircleIcon size={32} data-id="element-2095" />,
    role: 'Range Officer',
    path: '/permits',
    status: 'upcoming' as const
  }, {
    id: 7,
    title: 'Start Trip',
    description: 'QR/RFID scan at entry',
    icon: <PlayIcon size={32} data-id="element-2096" />,
    role: 'Field Officer',
    path: '/permits',
    status: 'upcoming' as const
  }, {
    id: 8,
    title: 'End Trip',
    description: 'QR/RFID scan at exit',
    icon: <StopCircleIcon size={32} data-id="element-2097" />,
    role: 'Field Officer',
    path: '/permits',
    status: 'upcoming' as const
  }, {
    id: 9,
    title: 'Monitoring & Reporting',
    description: 'Track and analyze data',
    icon: <BarChart3Icon size={32} data-id="element-2098" />,
    role: 'HQ Admin',
    path: '/reports',
    status: 'upcoming' as const
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 border-green-500 text-green-900';
      case 'current':
        return 'bg-blue-100 border-blue-500 text-blue-900';
      case 'upcoming':
        return 'bg-gray-100 border-gray-300 text-gray-600';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-600';
    }
  };
  return <div className="space-y-6" data-id="element-2099">
      <div data-id="element-2100">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2101">Complete User Flow</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2102">
          End-to-end journey from registration to monitoring
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2103">
        <p className="text-sm text-blue-900" data-id="element-2104">
          <strong data-id="element-2105">How to use:</strong> This diagram shows the complete workflow
          from beneficiary registration to trip monitoring. Click on any step to
          navigate to that module.
        </p>
      </div>

      {/* Horizontal Flow */}
      <div className="bg-white rounded-lg border border-gray-200 p-8 overflow-x-auto" data-id="element-2106">
        <div className="flex items-center gap-4 min-w-max" data-id="element-2107">
          {steps.map((step, idx) => <Fragment key={step.id} data-id="element-2108">
              <button onClick={() => navigate(step.path)} className={`flex-shrink-0 w-64 border-2 rounded-lg p-6 text-left transition-all hover:shadow-lg ${getStatusColor(step.status)}`} data-id="element-2109">
                <div className="flex items-start gap-4 mb-3" data-id="element-2110">
                  <div className={`p-3 rounded-lg ${step.status === 'completed' ? 'bg-green-200' : step.status === 'current' ? 'bg-blue-200' : 'bg-gray-200'}`} data-id="element-2111">
                    {step.icon}
                  </div>
                  <div className="flex-1" data-id="element-2112">
                    <div className="text-xs font-semibold uppercase mb-1" data-id="element-2113">
                      Step {step.id}
                    </div>
                    <h3 className="font-bold text-lg mb-1" data-id="element-2114">{step.title}</h3>
                  </div>
                </div>
                <p className="text-sm mb-3" data-id="element-2115">{step.description}</p>
                <div className="flex items-center justify-between" data-id="element-2116">
                  <span className="text-xs font-medium" data-id="element-2117">{step.role}</span>
                  {step.status === 'completed' && <CheckCircleIcon size={20} className="text-green-600" data-id="element-2118" />}
                  {step.status === 'current' && <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" data-id="element-2119"></div>}
                </div>
              </button>
              {idx < steps.length - 1 && <ArrowRightIcon className="flex-shrink-0 text-gray-400" size={32} data-id="element-2120" />}
            </Fragment>)}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2121">
        <h3 className="font-semibold text-gray-900 mb-4" data-id="element-2122">Status Legend</h3>
        <div className="flex gap-6" data-id="element-2123">
          <div className="flex items-center gap-2" data-id="element-2124">
            <div className="w-4 h-4 bg-green-500 rounded" data-id="element-2125"></div>
            <span className="text-sm text-gray-700" data-id="element-2126">Completed</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2127">
            <div className="w-4 h-4 bg-blue-500 rounded" data-id="element-2128"></div>
            <span className="text-sm text-gray-700" data-id="element-2129">Current Step</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2130">
            <div className="w-4 h-4 bg-gray-300 rounded" data-id="element-2131"></div>
            <span className="text-sm text-gray-700" data-id="element-2132">Upcoming</span>
          </div>
        </div>
      </div>
    </div>;
}